import { useState } from "react";
import { toast } from "sonner";
import { supervisorApi } from "@/lib/SupervisorApi";
import { Modal, Field, inputCls } from "./SupervisorPrimitives";

/**
 * Create-a-new-tenant flow. On submit calls
 * ``POST /api/supervisor/tenants`` and closes on success via ``onCreated``.
 * Field-level ``pattern="[0-9]{6,12}"`` gate keeps 4-digit-cashier-style
 * PINs out of the manager slot; the backend also enforces uniqueness
 * and rejects known-weak PINs like ``26502650`` (SEC-004).
 */
export default function CreateTenantDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    tenant_code: "",
    business_name: "",
    business_afm: "",
    business_address: "",
    business_phone: "",
    business_email: "",
    plan: "premium",
    super_manager_pin: "",
    trial_days: 30,
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await supervisorApi.createTenant(form);
      toast.success(`Δημιουργήθηκε ο πελάτης «${form.business_name}»`);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Σφάλμα δημιουργίας");
    } finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Modal title="Νέος Πελάτης" onClose={onClose}>
      <form onSubmit={submit}>
        <Field label="Κωδικός Καταστήματος *" hint="Μοναδικός, γράμματα/αριθμοί/_/-. Θα γίνει UPPERCASE.">
          <input required value={form.tenant_code} onChange={set("tenant_code")} placeholder="TAVERNA_KOSTAS" className={inputCls + " font-mono uppercase"} data-testid="create-tenant-code" />
        </Field>
        <Field label="Επωνυμία Επιχείρησης *">
          <input required value={form.business_name} onChange={set("business_name")} placeholder="Ταβέρνα ο Κώστας" className={inputCls} data-testid="create-tenant-name" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="ΑΦΜ">
            <input value={form.business_afm} onChange={set("business_afm")} className={inputCls} />
          </Field>
          <Field label="Τηλέφωνο">
            <input value={form.business_phone} onChange={set("business_phone")} className={inputCls} />
          </Field>
        </div>
        <Field label="Διεύθυνση">
          <input value={form.business_address} onChange={set("business_address")} className={inputCls} />
        </Field>
        <Field label="Email">
          <input type="email" value={form.business_email} onChange={set("business_email")} className={inputCls} />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Plan *">
            <select value={form.plan} onChange={set("plan")} className={inputCls}>
              <option value="basic">Basic</option>
              <option value="premium">Premium</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </Field>
          <Field label="Super Mgr PIN * (4-8 ψηφία, όχι default)" hint="Αποφύγετε 26502650, 2650, 12345678, ίδια ψηφία">
            <input required minLength={4} maxLength={8} pattern="[0-9]{4,8}" value={form.super_manager_pin} onChange={set("super_manager_pin")} className={inputCls + " font-mono"} data-testid="create-tenant-pin" placeholder="π.χ. 73914628" />
          </Field>
          <Field label="Trial (ημ.)">
            <input type="number" min="0" max="365" value={form.trial_days} onChange={set("trial_days")} className={inputCls + " font-mono"} />
          </Field>
        </div>
        <Field label="Σημειώσεις">
          <textarea value={form.notes} onChange={set("notes")} className={inputCls + " h-20 py-2"} />
        </Field>
        <div className="flex gap-2 justify-end pt-4 border-t border-zinc-800 -mx-6 px-6">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm font-bold">
            Άκυρο
          </button>
          <button type="submit" disabled={busy} data-testid="create-tenant-submit"
            className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm disabled:opacity-40">
            {busy ? "Δημιουργία..." : "Δημιουργία Πελάτη"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
